/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { SearchParams } from '../context/PartnershipsWizardContext';
import {
  propertyCityState,
  propertyCategory,
  propertyTransactionTypes,
  propertyPricing,
} from '@/shared/utils/property-display';

const CATEGORY_TO_SEARCH: Record<string, string> = {
  'Casa': 'casas',
  'Apartamento': 'apartamentos',
  'Terreno': 'terrenos',
  'Cobertura': 'coberturas',
  'Predio Comercial': 'comerciais',
  'Sala Comercial': 'comerciais',
  Galpao: 'comerciais',
  Kitnet: 'apartamentos',
  Studio: 'apartamentos',
  Flat: 'apartamentos',
  Loft: 'apartamentos',
  Chacara: 'casas',
  Casa_Comercial: 'comerciais',
  Ponto_Comercial: 'comerciais',
  Garagem: 'comerciais',
  Fazenda: 'terrenos',
  Sitio: 'terrenos',
  Pousada: 'comerciais',
  Academia: 'comerciais',
  'Edifício': 'comerciais',
};

/**
 * Property transaction to partnership search transaction. An auction is a
 * purchase. Temporada has no counterpart in the search (it is short-term
 * letting, not Aluguel), so it seeds nothing rather than a rental search.
 */
const TRANSACTION_TO_SEARCH: Record<string, string> = {
  'Venda': 'venda',
  'Aluguel': 'aluguel',
  'Permuta': 'venda',
  'Leilão': 'venda',
};

const CLIENT_BUSINESS_TO_SEARCH: Record<string, string> = {
  'buy': 'venda',
  'rent': 'aluguel',
  'sale': 'venda',
  'rental': 'aluguel',
};

export const PRICE_STEP = 50000;

/**
 * Returns the first element of an array, or undefined when it is empty/absent.
 *
 * @param arr - The array to read from, possibly undefined.
 * @returns The first element, or undefined.
 */
function extractFirst<T>(arr: T[] | undefined): T | undefined {
  return arr && arr.length > 0 ? arr[0] : undefined;
}

/**
 * Derives a representative price from a property, preferring the sale price and
 * falling back to the rental price. Reads nested pricing first, then flat.
 *
 * @param property - The raw property record.
 * @returns The sale or rental price, or undefined when neither is present.
 */
function priceRange(property: Record<string, unknown>): number | undefined {
  const pricing = propertyPricing(property);
  return pricing.sale?.price || pricing.rental?.price || undefined;
}

/**
 * Copies a property's city/neighborhood into the search filter draft.
 * Reads the nested location VO first, then flat fallback.
 *
 * @param property - The raw property record.
 * @param filters - The filter draft mutated in place.
 */
function seedLocation(property: Record<string, unknown>, filters: Partial<SearchParams>) {
  const { city } = propertyCityState(property);
  if (city) filters.city = city;
  const loc = property.location as Record<string, unknown> | undefined;
  const neighborhood = (loc?.neighborhood as string) || (property.neighborhood as string);
  if (neighborhood) filters.neighbourhood = neighborhood;
}

/**
 * Seeds the search property type from a property's category, if known.
 */
function seedCategoryType(property: Record<string, unknown>, filters: Partial<SearchParams>): void {
  const category = propertyCategory(property);
  if (category) filters.property_type = CATEGORY_TO_SEARCH[category] || 'casas';
}

/**
 * Seeds the search transaction type from a property's first transaction type, if any.
 * A listing offered only as Temporada seeds nothing; one offering Temporada
 * beside another transaction seeds that other one.
 */
function seedTransactionType(property: Record<string, unknown>, filters: Partial<SearchParams>): void {
  const txTypes = propertyTransactionTypes(property).filter((tx) => tx !== 'Temporada');
  const firstTx = txTypes[0];
  if (firstTx) filters.transaction = TRANSACTION_TO_SEARCH[firstTx] || 'venda';
}

/**
 * Maps a property's category and transaction type onto the search filter draft.
 *
 * @param property - The raw property record.
 * @param filters - The filter draft mutated in place.
 */
function seedPropertyCategory(property: Record<string, unknown>, filters: Partial<SearchParams>) {
  seedCategoryType(property, filters);
  seedTransactionType(property, filters);
}

/**
 * Seeds bedroom count and an upper price bound (20% above list) from a property.
 *
 * @param property - The raw property record.
 * @param filters - The filter draft mutated in place.
 */
function seedPropertySize(property: Record<string, unknown>, filters: Partial<SearchParams>) {
  const chars = property.characteristics as Record<string, unknown> | undefined;
  const housing = property.housing as Record<string, unknown> | undefined;
  const beds = chars?.bedrooms ?? housing?.bedrooms;
  if (beds) filters.bedrooms = Number(beds);
  const price = priceRange(property);
  if (price) filters.price_max = Math.ceil((price * 1.2) / PRICE_STEP) * PRICE_STEP;
}

/**
 * Builds a partner-search filter draft seeded from a source property's
 * location, category, and size.
 *
 * @param property - The raw property record.
 * @returns A partial set of search parameters.
 */
export function seedFromProperty(property: Record<string, unknown>): Partial<SearchParams> {
  const filters: Partial<SearchParams> = {};
  seedLocation(property, filters);
  seedPropertyCategory(property, filters);
  seedPropertySize(property, filters);
  return filters;
}

/**
 * Copies a client's preferred cities/neighborhoods into the filter draft.
 *
 * @param prefs - The client's preference record.
 * @param filters - The filter draft mutated in place.
 */
function seedClientLocation(prefs: Record<string, unknown>, filters: Partial<SearchParams>) {
  const firstCity = extractFirst(prefs.cities as string[] | undefined);
  if (firstCity) filters.city = firstCity;
  const firstNeighborhood = extractFirst(prefs.neighborhoods as string[] | undefined);
  if (firstNeighborhood) filters.neighbourhood = firstNeighborhood;
}

/**
 * Maps a client's preferred property kind and business type onto the filter
 * draft.
 *
 * @param prefs - The client's preference record.
 * @param filters - The filter draft mutated in place.
 */
function seedClientCategory(prefs: Record<string, unknown>, filters: Partial<SearchParams>) {
  const firstKind = extractFirst(prefs.property_kinds as string[] | undefined);
  if (firstKind) filters.property_type = CATEGORY_TO_SEARCH[firstKind] || 'casas';
  const business = prefs.business_type as string | undefined;
  if (business) filters.transaction = CLIENT_BUSINESS_TO_SEARCH[business] || 'venda';
}

/**
 * Copies a client's min/max price preferences into the filter draft.
 *
 * @param prefs - The client's preference record.
 * @param filters - The filter draft mutated in place.
 */
function seedClientPrice(prefs: Record<string, unknown>, filters: Partial<SearchParams>) {
  if (prefs.price_min) filters.price_min = Number(prefs.price_min);
  if (prefs.price_max) filters.price_max = Number(prefs.price_max);
}

/**
 * Builds a partner-search filter draft seeded from a client's preferences
 * (location, category, and price range).
 *
 * @param client - The raw client record (used as a preferences fallback).
 * @param preferences - Explicit preferences overriding the client's own.
 * @returns A partial set of search parameters, or an empty object when no
 *   preferences are available.
 */
export function seedFromClient(
  client: Record<string, unknown>,
  preferences?: Record<string, unknown>,
): Partial<SearchParams> {
  const prefs = preferences || (client.preferences as Record<string, unknown> | undefined);
  if (!prefs) return {};
  const filters: Partial<SearchParams> = {};
  seedClientLocation(prefs, filters);
  seedClientCategory(prefs, filters);
  seedClientPrice(prefs, filters);
  return filters;
}
